/**
 * Footer — rodapé compartilhado.
 *
 * Layout:
 *  - Esquerda: © + selos LGPD (→ /privacidade) + SSL (→ /status)
 *  - Direita:  link "Termos" (único sem selo equivalente)
 *
 * Selos comunicam compliance visualmente e já fazem o papel de link.
 * Mantemos só "Termos" no rodapé direito pra evitar duplicação.
 */
"use client";

import Link from "next/link";
import { useTranslation } from "react-i18next";


export interface FooterProps {
  variant?: "default" | "minimal";
}


function ShieldIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}


function Selo({ href, icon, title, desc }: { href: string; icon: React.ReactNode; title: string; desc: string }) {
  return (
    <Link
      href={href}
      title={desc}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-app-subtle bg-app-soft hover:border-app-strong hover:bg-app-hover transition-colors"
    >
      <span className="text-app-2">{icon}</span>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-app-2">{title}</span>
    </Link>
  );
}


export function Footer({ variant = "default" }: FooterProps) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const baseClass = variant === "minimal"
    ? "w-full py-4 px-4"
    : "w-full border-t border-app-subtle py-4 px-4 sm:px-6 lg:px-10";

  return (
    <footer className={baseClass}>
      <div className="max-w-[1400px] mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-app-3">
        {/* Esquerda: copyright + selos clicáveis */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span>© {year} Miller Solutions</span>
          <div className="flex items-center gap-1.5">
            <Selo
              href="/privacidade"
              icon={<ShieldIcon />}
              title={t("footer.lgpdReady", "LGPD")}
              desc={t("footer.lgpdReadyDesc", "Em conformidade com a LGPD. Clique para ver a política de privacidade.")}
            />
            <Selo
              href="/status"
              icon={<LockIcon />}
              title={t("footer.sslSecured", "SSL")}
              desc={t("footer.sslSecuredDesc", "Conexão criptografada (TLS). Clique para ver o status dos serviços.")}
            />
          </div>
        </div>

        {/* Direita: único link sem selo equivalente */}
        <nav className="flex items-center">
          <Link href="/termos" className="hover:text-app-2 transition-colors">
            {t("footer.terms", "Termos")}
          </Link>
        </nav>
      </div>
    </footer>
  );
}
